import React, { useEffect, useRef } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";

const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

const NAV_LINKS = [
  { to: "/cockpit/dashboard",    icon: "📊", label: "Dashboard",     roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/transactions", icon: "💳", label: "Transactions",  roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/collections",  icon: "📥", label: "Collections",   roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/snpl-decisions", icon: "🧠", label: "SNPL Decisions", roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/fx-config",    icon: "💱", label: "FX Config",     roles: ["super_admin","finance_admin"] },
  { to: "/cockpit/wallets",      icon: "💰", label: "Wallets",       roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/compliance",   icon: "🛡️", label: "Customers & Compliance",    roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/kyc",          icon: "📋", label: "KYC Verification",    roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/providers",    icon: "🔌", label: "Providers",     roles: ["super_admin","finance_admin","ops_admin"] },
  { to: "/cockpit/audit",        icon: "📋", label: "Audit Logs",    roles: ["super_admin","finance_admin"] },
  { to: "/cockpit/team",         icon: "👥", label: "Team & Admins", roles: ["super_admin"] },
];

export default function CockpitLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef(null);
  const navigateRef = useRef(navigate);

  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    const doLogout = () => {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      localStorage.removeItem("cockpitLastActivity");
      navigateRef.current("/cockpit/login", {
        replace: true,
        state: { message: "You were logged out due to 5 minutes of inactivity." }
      });
    };

    const isExpired = () => {
      const last = Number(localStorage.getItem("cockpitLastActivity") || Date.now());
      return Date.now() - last >= INACTIVITY_TIMEOUT;
    };

    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const last = Number(localStorage.getItem("cockpitLastActivity") || Date.now());
      const remaining = Math.max(INACTIVITY_TIMEOUT - (Date.now() - last), 0);
      timerRef.current = setTimeout(() => {
        if (isExpired()) doLogout();
        else schedule();
      }, remaining);
    };

    const reset = () => {
      localStorage.setItem("cockpitLastActivity", String(Date.now()));
      schedule();
    };

    const checkNow = () => {
      if (isExpired()) doLogout();
      else schedule();
    };

    if (!localStorage.getItem("cockpitLastActivity")) {
      localStorage.setItem("cockpitLastActivity", String(Date.now()));
    }

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach(e => window.addEventListener(e, reset));
    window.addEventListener("focus", checkNow);
    document.addEventListener("visibilitychange", checkNow);

    checkNow();

    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      window.removeEventListener("focus", checkNow);
      document.removeEventListener("visibilitychange", checkNow);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const adminUser = (() => {
    try { return JSON.parse(localStorage.getItem("adminUser") || "{}"); }
    catch { return {}; }
  })();

  const handleLogout = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigateRef.current("/cockpit/login", { replace: true });
  };

  const pageTitle = NAV_LINKS.find(n => location.pathname.startsWith(n.to))?.label || "Cockpit";

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>
      {/* Sidebar */}
      <aside style={{ width: "220px", background: "#111827", color: "#fff", padding: "0", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        {/* Logo */}
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #374151" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#fff" }}>ZoomPay</div>
          <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>Admin Cockpit</div>
        </div>

        {/* Nav */}
        <nav style={{ padding: "12px 8px", flex: 1 }}>
          {NAV_LINKS.filter(n => !n.roles || n.roles.includes(adminUser.role)).map(({ to, icon, label }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link key={to} to={to} style={{
                display: "flex", alignItems: "center", gap: "10px",
                padding: "9px 12px", borderRadius: "8px", marginBottom: "2px",
                color: active ? "#fff" : "#9ca3af",
                background: active ? "#1d4ed8" : "transparent",
                textDecoration: "none", fontSize: "14px", fontWeight: active ? "600" : "400",
                transition: "all 0.15s"
              }}>
                <span style={{ fontSize: "16px" }}>{icon}</span>
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Admin info at bottom of sidebar */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid #374151" }}>
          <div style={{ fontSize: "11px", color: "#6b7280" }}>Signed in as</div>
          <div style={{ fontSize: "13px", color: "#fff", fontWeight: "600", marginTop: "2px" }}>
            {adminUser.name || "Admin"}
          </div>
          <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "1px", wordBreak: "break-all" }}>
            {adminUser.email || "admin"}
          </div>
          <div style={{ fontSize: "11px", color: "#4b5563", marginTop: "2px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {adminUser.role || "admin"}
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top header bar */}
        <header style={{
          height: "56px", background: "#fff", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 24px", flexShrink: 0, position: "sticky", top: 0, zIndex: 10
        }}>
          <div style={{ fontSize: "16px", fontWeight: "600", color: "#111827" }}>
            {NAV_LINKS.find(n => location.pathname.startsWith(n.to))?.icon} {pageTitle}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={handleLogout}
              style={{
                padding: "7px 16px", background: "#dc2626", color: "white",
                border: "none", borderRadius: "6px", cursor: "pointer",
                fontWeight: "600", fontSize: "13px", display: "flex",
                alignItems: "center", gap: "6px"
              }}
            >
              🚪 Logout
            </button>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflow: "auto", background: "#f9fafb" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
